import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';

interface OnboardingSpotlightProps {
    targetSelector: string;
    title: string;
    description: string;
    buttonText: string;
    onNext: () => void;
}

interface Rect {
    top: number;
    left: number;
    width: number;
    height: number;
}

const PADDING = 8;

export const OnboardingSpotlight = ({
    targetSelector,
    title,
    description,
    buttonText,
    onNext,
}: OnboardingSpotlightProps) => {
    const [rect, setRect] = useState<Rect | null>(null);

    const updateRect = useCallback(() => {
        const el = document.querySelector(targetSelector);
        if (!el) return;
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }, [targetSelector]);

    useEffect(() => {
        updateRect();

        const el = document.querySelector(targetSelector);
        if (!el) return;

        const observer = new ResizeObserver(updateRect);
        observer.observe(el);

        window.addEventListener('scroll', updateRect, true);
        window.addEventListener('resize', updateRect);

        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', updateRect, true);
            window.removeEventListener('resize', updateRect);
        };
    }, [targetSelector, updateRect]);

    if (!rect) return null;

    const cutoutTop = rect.top - PADDING;
    const cutoutLeft = rect.left - PADDING;
    const cutoutWidth = rect.width + PADDING * 2;
    const cutoutHeight = rect.height + PADDING * 2;

    // Position the card below the target, or above if not enough space
    const spaceBelow = window.innerHeight - (rect.top + rect.height + PADDING);
    const cardBelow = spaceBelow > 200;
    const cardStyle: React.CSSProperties = cardBelow
        ? { top: rect.top + rect.height + PADDING + 12, left: Math.max(16, rect.left) }
        : { bottom: window.innerHeight - rect.top + PADDING + 12, left: Math.max(16, rect.left) };

    return (
        <div className="fixed inset-0 z-50">
            {/* Spotlight cutout overlay */}
            <div
                className="absolute rounded-lg"
                style={{
                    top: cutoutTop,
                    left: cutoutLeft,
                    width: cutoutWidth,
                    height: cutoutHeight,
                    boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
                    pointerEvents: 'none',
                }}
            />
            {/* Click blocker behind the card */}
            <div className="absolute inset-0" onClick={e => e.stopPropagation()} />
            {/* Explanation card */}
            <div
                className="absolute max-w-xs rounded-lg bg-background p-4 shadow-xl"
                style={cardStyle}
            >
                <h3 className="mb-2 text-sm font-semibold dark:text-white">{title}</h3>
                <p className="mb-4 text-sm text-muted-foreground">{description}</p>
                <Button onClick={onNext} size="sm" className="rounded-full">
                    {buttonText}
                </Button>
            </div>
        </div>
    );
};
